<template>
  <div class="text-center">
    <v-tooltip v-if="fileType === 'UNI'" top>
      <template v-slot:activator="{ on }">
        <div>
          <v-btn
            :id="`add-instance-${fileType}`"
            class="ctp-btn mx-3 my-2 px-8 disabled"
            rounded
            dark
            v-on="on"
          >
            {{ $t('map.tolldomains.dsrc_instances.add_instance') }}
          </v-btn>
        </div>
      </template>
      {{ $t('map.tolldomains.dsrc_instances.add_instance_disabled') }}
    </v-tooltip>
    <v-tooltip v-else-if="getNonArchivedDatInstances.length >= 11" top>
      <template v-slot:activator="{ on }">
        <div>
          <v-btn
            :id="`add-instance-${fileType}`"
            class="ctp-btn mx-3 my-2 px-8 disabled"
            rounded
            dark
            v-on="on"
          >
            {{ $t('map.tolldomains.dsrc_instances.add_instance') }}
          </v-btn>
        </div>
      </template>
      {{ $t('map.tolldomains.dsrc_instances.max_instance_reached') }}
    </v-tooltip>
    <v-btn
      v-else
      :id="`add-instance-${fileType}`"
      class="ctp-btn mx-3 my-2 px-8"
      rounded
      dark
      @click="dialog = !dialog"
    >
      {{ $t('map.tolldomains.dsrc_instances.add_instance') }}
    </v-btn>
    <v-dialog v-model="dialog" width="600" dark>
      <v-card id="add-instance-dialog" class="dat-instance-container">
        <v-card-title class="title px-12">{{ $t('map.tolldomains.dsrc_instances.add_instance_title', { type: fileType }) }}</v-card-title>
        <v-divider dark />
        <v-card-text class="content py-7 px-12">
          <v-form ref="formValid" v-model="valid">
            <v-row class="d-flex flex-row" dense justify="center">
              <v-col cols="9">
                <div class="mb-1 instance-label required">{{ $t('map.tolldomains.dsrc_instances.headers.instance_name') }}</div>
                <v-text-field
                  id="add-instance-name"
                  v-model.trim="newInstance.name"
                  outlined
                  dark
                  dense
                  hide-details="auto"
                  :rules="nameRules"
                />
              </v-col>
              <v-col cols="3">
                <div class="mb-1 instance-label required">{{ $t('map.tolldomains.dsrc_instances.headers.eid') }}</div>
                <v-text-field
                  id="add-instance-eid"
                  v-model.number="newInstance.eid"
                  outlined
                  dark
                  dense
                  :rules="eidRules"
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <v-row class="d-flex flex-column mt-8" dense justify="center">
              <div class="mb-1 instance-label required">{{ $t('map.tolldomains.dsrc_instances.headers.dat_associated') }}</div>
              <v-select
                id="add-instance-dat"
                v-model="newInstance.dat"
                :items="datFiles"
                outlined
                dark
                dense
                :rules="requiredRules"
                :color="colors.secondary"
                :item-text="item =>`${item.id} - ${item.name}`"
                :item-value="item => ({ id: item.id, name: item.name })"
                hide-details
                :no-data-text="$t('common.no_data')"
                clearable
              />
            </v-row>
            <v-divider />
            <span id="parameters">{{ $t('common.parameters') }}</span>
            <span id="dsrc-creds">{{ $t('map.tolldomains.dsrc_instances.dsrc_creds') }}</span>
            <v-row class="d-flex flex-column mt-3" dense justify="center">
              <div class="mb-1 instance-label required">{{ $t('map.tolldomains.dsrc_instances.transaction_security') }}</div>
              <v-select
                id="add-instance-transac-sec"
                v-model="newInstance.transactionSecurity"
                :items="transactionSecurityEnum"
                outlined
                dark
                dense
                :rules="requiredRules"
                :color="colors.secondary"
                hide-details
                :no-data-text="$t('common.no_data')"
                clearable
              />
            </v-row>
            <v-row class="d-flex flex-column mt-3" dense justify="center">
              <div class="mb-1 instance-label required">{{ $t('map.tolldomains.dsrc_instances.contextMarkPriority') }}</div>
              <v-text-field
                id="add-instance-ctx-mark-prio"
                v-model.number="newInstance.contextMarkPriority"
                outlined
                dark
                dense
                :rules="contextMarkRules"
                hide-details="auto"
              />
            </v-row>
            <v-row class="d-flex flex-column mt-3" dense justify="center">
              <div class="mb-1 instance-label">{{ $t('map.tolldomains.dsrc_instances.forced_return_status') }}</div>
              <v-radio-group
                v-model="newInstance.forcedReturnStatus"
                row
                class="pa-0 ma-0"
                mandatory
                hide-details
              >
                <v-row>
                  <v-col cols="2" class="ml-2 pt-2">
                    <v-radio
                      id="add-instance-forced-return-off"
                      slot="label"
                      color="primary"
                      dark
                      value="off"
                    >
                      <template slot="label">
                        <div>{{ $t('map.tolldomains.dsrc_instances.return_status_off') }}</div>
                      </template>
                    </v-radio>
                  </v-col>
                  <v-col cols="2" class="ml-2 pt-2">
                    <v-radio
                      id="add-instance-forced-return-on"
                      slot="label"
                      color="primary"
                      dark
                      value="on"
                    >
                      <template slot="label">
                        <div>{{ $t('map.tolldomains.dsrc_instances.return_status_on') }}</div>
                      </template>
                    </v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-row>
            <v-row class="d-flex flex-column my-3" dense justify="center">
              <div class="mb-1 vst-header">{{ $t('map.tolldomains.dsrc_instances.vst_security_level') }}</div>
              <v-radio-group
                v-model="newInstance.vst"
                density="compact"
                row
                class="pa-0 ma-0"
                mandatory
                hide-details
              >
                <v-row>
                  <v-col cols="3" class="ml-2 pt-2">
                    <v-radio
                      id="add-instance-vst-default"
                      slot="label"
                      color="primary"
                      dark
                      value="default"
                    >
                      <template slot="label">
                        <div>{{ $t('map.tolldomains.dsrc_instances.vst_default') }}</div>
                      </template>
                    </v-radio>
                  </v-col>
                  <v-col cols="3" class="ml-2 pt-2">
                    <v-radio
                      id="add-instance-vst-0"
                      slot="label"
                      color="primary"
                      dark
                      value="level0"
                    >
                      <template slot="label">
                        <div>{{ $t('map.tolldomains.dsrc_instances.vst_level0') }}</div>
                      </template>
                    </v-radio>
                  </v-col>
                  <v-col cols="3" class="ml-2 pt-2">
                    <v-radio
                      id="add-instance-vst-1"
                      slot="label"
                      color="primary"
                      dark
                      value="level1"
                    >
                      <template slot="label">
                        <div>{{ $t('map.tolldomains.dsrc_instances.vst_level1') }}</div>
                      </template>
                    </v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-row>
            <span id="ban-mode">{{ $t('map.tolldomains.dsrc_instances.ban_mode') }}</span>
            <v-row class="mb-10" dense>
              <div class="instance-label mr-6">{{ $t('map.tolldomains.dsrc_instances.keep_instance_active') }}</div>
              <v-switch
                id="add-instance-ban-activated"
                v-model="newInstance.banActivated"
                class="activation-switch"
                color="primary"
                flat
                inset
              />
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider dark />
        <v-card-text class="px-12 pb-0 add-file-actions">
          <v-btn
            id="add-instance-add"
            class="ctp-btn"
            dark
            :color="colors.secondary"
            :disabled="!valid"
            @click="addInstance"
          >
            {{ $t('common.add') }}
          </v-btn>
          <v-btn
            id="add-instance-cancel"
            class="cancel"
            dark
            rounded
            @click="close"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { DatV2, InstanceV2, VSTSecurityLevelEnum, ForcedReturnStatusEnum, TransactionSecurityEnum } from 'ctp-toll-domain';
import { store } from '@/store/modules/datInstances/types';
import { colors } from '@/constants/theme/colors';
import { DAT_INSTANCE_NAME_MAX_LENGTH, DAT_INSTANCE_NAME_FORBIDEN_REGEXP, DAT_INSTANCE_EID_REGEXP, DAT_INSTANCE_CONTEXTMARK_REGEXP } from '@/constants/components/datinstances';
import Validation from '@/mixins/Validation';
import { MasterAccountInfo } from 'ctp-user-auth-service';

@Component
export default class AddInstanceDialog extends Mixins(Validation) {
  @Prop()
  private datFiles!: DatV2[];

  @Prop()
  private fileType!: string;

  @Prop()
  private activefileCount!: string;

  @Prop()
  private fileTotal!: string;

  @Prop()
  private masterAccount!: MasterAccountInfo;

  @store.Getter
  private getNonArchivedDatInstances: () => InstanceV2[];

  private colors = colors;

  private dialog = false;

  private valid = false;

  private newInstance: any = {
    name: '',
    banActivated: false,
    eid: null,
    transactionSecurity: TransactionSecurityEnum.Default,
    contextMarkPriority: 0,
    forcedReturnStatus: ForcedReturnStatusEnum.Default,
    vst: VSTSecurityLevelEnum.Default
  };

  private nameRules = [
    this.maxLengthRule(DAT_INSTANCE_NAME_MAX_LENGTH, 'common.error.forms.inputs.validator.max_length_field'),
    this.regexpRule(DAT_INSTANCE_NAME_FORBIDEN_REGEXP, 'common.error.forms.inputs.validator.forbidden_chars'),
    this.requiredRule('common.error.forms.inputs.validator.mandatory')
  ];

  private eidRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.regexpRule(DAT_INSTANCE_EID_REGEXP, 'common.error.forms.inputs.validator.only_digits'),
    this.valueExclude(128, 'common.error.forms.inputs.validator.value_reserved'),
    this.range(1, 29, 'common.error.forms.inputs.validator.eid')
  ];

  private requiredRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory')
  ];

  private contextMarkRules = [
    this.requiredRule('common.error.forms.inputs.validator.mandatory'),
    this.regexpRule(DAT_INSTANCE_CONTEXTMARK_REGEXP, 'common.error.forms.inputs.validator.context_mark')
  ];

  private transactionSecurityEnum = ['open', 'pwd', 'des', '3des', 'aes', 'default'];

  private addInstance() {
    const payload: any = {
      name: this.newInstance.name,
      masterId: this.masterAccount.id,
      eid: Number(this.newInstance.eid),
      transactionSecurity: this.newInstance.transactionSecurity,
      contextMarkPriority: this.newInstance.contextMarkPriority,
      forcedReturnStatus: this.newInstance.forcedReturnStatus,
      vst: this.newInstance.vst,
      banActivated: this.newInstance.banActivated,
      type: this.fileType.toLowerCase()
    };
    if (this.newInstance.dat) {
      payload.datId = this.newInstance.dat.id;
    }
    this.$emit('addInstance', payload);
    this.close();
  }

  private close() {
    (this.$refs.formValid as HTMLFormElement).reset();
    this.dialog = false;
  }
}
</script>

<style lang="scss" scoped>
@import './styles/add-instance.scss';
</style>
